import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, CreditCard, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { boPath } from '@/utils/getBasePath';

const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : '');

const seenKey = (w) => `chain29_billing_warning_${w.invoice_number}_${w.locked ? 'locked' : 'warn'}`;
const seen = (w) => { try { return sessionStorage.getItem(seenKey(w)) === '1'; } catch { return false; } };
const markSeen = (w) => { try { sessionStorage.setItem(seenKey(w), '1'); } catch { /* private window */ } };

/**
 * Two autopay charges failed (owner, Nov 5 2026: "2 time fail pop up warning of 7 days account
 * softlock"): a pop-up names the invoice and the lock date, and a bar stays on every page. From
 * the lock date until the invoice is paid the back office opens only Billing. The register,
 * kitchen screen and kiosk are not part of the back office and keep working.
 */
export default function BillingGate({ warning }) {
  const location = useLocation();
  const navigate = useNavigate();
  const billing = boPath('settings/billing');
  const onBilling = location.pathname === billing || location.pathname.endsWith('/settings/billing');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (warning && !seen(warning)) setOpen(true);
  }, [warning]);

  useEffect(() => {
    if (warning?.locked && !onBilling) navigate(billing, { replace: true });
  }, [warning, onBilling, billing, navigate]);

  if (!warning) return null;
  const close = () => { markSeen(warning); setOpen(false); };
  const goPay = () => { close(); navigate(billing); };

  return (
    <>
      <div
        className={`mb-4 rounded-lg border p-3 text-sm flex flex-col sm:flex-row sm:items-center gap-2 ${
          warning.locked ? 'border-red-300 bg-red-50 text-red-800' : 'border-amber-300 bg-amber-50 text-amber-900'}`}
        data-testid="billing-warning-bar"
      >
        {warning.locked ? <Lock className="h-4 w-4 flex-shrink-0" /> : <AlertTriangle className="h-4 w-4 flex-shrink-0" />}
        <span className="flex-1">
          {warning.locked
            ? `Your account is limited to Billing until invoice ${warning.invoice_number} (${money(warning.amount_cents)}) is paid.`
            : `Payment for invoice ${warning.invoice_number} (${money(warning.amount_cents)}) failed ${warning.attempts} times. Update your card by ${day(warning.lock_at)} or your back office will be limited to Billing.`}
        </span>
        {!onBilling && (
          <Button size="sm" onClick={goPay} className="min-h-[40px]">Update card</Button>
        )}
      </div>
      <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-md" data-testid="billing-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {warning.locked ? <Lock className="h-5 w-5 text-red-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
              {warning.locked ? 'Your account is limited' : 'Your payment failed twice'}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-gray-700">
                <p>
                  We could not charge {money(warning.amount_cents)} for invoice {warning.invoice_number}
                  {warning.reason ? ` (${warning.reason})` : ''}.
                </p>
                {warning.locked ? (
                  <p>The back office opens only Billing until the invoice is paid. Your POS, kitchen screens and kiosk keep working.</p>
                ) : (
                  <p data-testid="billing-warning-lock-date">
                    Your account will be soft locked on <strong>{day(warning.lock_at)}</strong>, 7 days after the second
                    failed charge: from then the back office opens only Billing until the invoice is paid.
                    We keep trying your card once a day.
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            {!warning.locked && <Button variant="outline" onClick={close} className="min-h-[44px]">Remind me later</Button>}
            <Button onClick={goPay} className="min-h-[44px]" data-testid="billing-warning-pay">
              <CreditCard className="h-4 w-4 mr-2" />Update card or pay now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
